// src/components/AuthProvider.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastVisited, setLastVisited] = useState("/inventory");

  /* -------------------------------------------------
   * Helper to inject / clear CSRF token in Axios
   * ------------------------------------------------*/
  const setAxiosCsrfHeader = (token) => {
    ["post", "put", "patch", "delete"].forEach((verb) => {
      axios.defaults.headers[verb] = {
        ...axios.defaults.headers[verb],
        "X-CSRF-Token": token,
      };
    });
  };

  const clearAxiosCsrfHeader = () => {
    ["post", "put", "patch", "delete"].forEach((verb) => {
      if (axios.defaults.headers[verb]) {
        delete axios.defaults.headers[verb]["X-CSRF-Token"];
      }
    });
  };

  /* -------------------------------------------------
   *  Auth helpers
   * ------------------------------------------------*/
  const fetchCsrfToken = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/csrf-token", {
        withCredentials: true,
      });
      setAxiosCsrfHeader(data.csrfToken);
    } catch (err) {
      console.error("Failed to fetch CSRF token:", err);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const res = await axios.post(
        "/api/user/login",
        { email, password },
        { withCredentials: true }
      );
      if (res.data.message === "Logged in") {
        setUser(res.data.user);
        setIsAuthenticated(true);
        await fetchCsrfToken(); // token tied to this fresh session
        return true;
      }
      return false;
    } catch (err) {
      throw new Error(
        err.response?.data.error ||
          "Login failed. Please check your credentials."
      );
    }
  };

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get("/api/user/check-auth", {
        withCredentials: true,
      });
      if (res.data.isAuthenticated) {
        setUser(res.data.user);
        setIsAuthenticated(true);
        await fetchCsrfToken();
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error("Failed to check authentication:", err);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [fetchCsrfToken]);

  const logout = async () => {
    try {
      await axios.post("/api/user/logout", {}, { withCredentials: true });
    } catch (err) {
      console.error("Failed to logout:", err);
    } finally {
      clearAxiosCsrfHeader();
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  /* -------------------------------------------------
   *  Lifecycle
   * ------------------------------------------------*/
  useEffect(() => {
    checkAuth(); // now safe
  }, [checkAuth]);

  /* -------------------------------------------------
   *  Misc helpers
   * ------------------------------------------------*/
  const updateLastVisited = (path) => setLastVisited(path);

  /* -------------------------------------------------
   *  Context value
   * ------------------------------------------------*/
  const value = {
    user,
    isAuthenticated,
    isLoading,
    lastVisited,
    login,
    logout,
    updateLastVisited,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
